const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/logActions.ts';
import Imm from 'immutable';
import { apiWriteClientLogEntry, ClientLogKind } from '@cistec/common_web/actions/kisapi/utils';
import { KISAPI as API } from '../middleware/kisapi';
import { getMainConfig } from '../services/mainConfig';
import { kisapiActionTypes } from '../defs/actionTypes';
import { LogSessionMeta } from '../sagas/loggingSaga';
import { Logger } from '../services/KisLoggerService';
import { assertNever } from '../utils/generalUtils';

const Log = Logger('logActions');

export const atPushLog = 'COMMON_WEB_PUSH_LOG';
export const atWriteLogEntries = kisapiActionTypes('COMMON_WEB_WRITE_LOG_ENTRIES');

export type LogSeverity = 'info' | 'warning' | 'error';

export interface LogEntry {
    severity: LogSeverity;
    message: string;
    details?: unknown;
}

/**
 * Writes the log entry to console and schedules it to be pushed to the server. loggingSaga will call
 * writeLogEntries when it sees fit.
 * @param severity whether this is just a debug log ('info'), a warning ('warning'), or a critical error ('error')
 * @param message describes what went wrong
 * @param details to further describe the error; can be an object
 */
export function pushLog(severity: LogSeverity, message: string, details: unknown) {
    const entry: LogEntry = {
        severity,
        message,
        details,
    };

    const msg = `pushLog: ${message}`;

    switch (severity) {
        case 'info':
            Log.info(msg);
            break;
        case 'warning':
            Log.warn(msg);
            break;
        case 'error':
            Log.error(msg);
            break;
        default:
            assertNever(severity);
    }

    return {
        type: atPushLog,
        entry,
    };
}

export type PushLogAction = ReturnType<typeof pushLog>;

/**
 * Action to write a number of log entries to the ClientLog. This is called by loggingSaga when the entries that have
 * been scheduled by pushLog need to be written.
 * @param logKind kind of ClientLog entry to write
 * @param logEntries one or more log entries
 * @param metaData browser information, etc. that are expected stay the same during the whole session
 * @param sessionUUID (optional) added by loggingSaga to identify user sessions
 */
export function writeLogEntries(
    logKind: ClientLogKind,
    logEntries: LogEntry[],
    metaData: LogSessionMeta,
    sessionUUID?: string
) {
    const cfg = getMainConfig();

    let severities = Imm.Set<string>();

    logEntries.forEach(entry => {
        severities = severities.add(entry.severity);
    });

    const joinedSeverities = severities.join(',');

    const content = {
        severity: joinedSeverities,
        sessionUUID,
        ...metaData,
        product: metaData.product || cfg.product,
        productVersion: metaData.productVersion || '',
        entries: logEntries,
    };

    // The summary will appear on the ClientLog's VAL column and will be truncated to 40 chars
    const summary = `${content.product} ${joinedSeverities} (${logEntries.length}) ${(sessionUUID || '').slice(0, 7)}`;

    const meta = {
        suppressErrorMsg: true,
        content,
        summary,
    };

    return {
        type: atWriteLogEntries.combined,
        [API]: apiWriteClientLogEntry(logKind, summary, content),
        meta,
    };
}
